import React, { useState, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

interface LanguageDropdownProps {
  value: string;
  onChange: (language: string) => void;
  buttonLabel?: string;
  className?: string;
}

// Expanded language list with more options
const languageOptions = [
  { code: 'en', name: 'English', emoji: '🇬🇧' },
  { code: 'en-US', name: 'English (United States)', emoji: '🇺🇸' },
  { code: 'en-GB', name: 'English (United Kingdom)', emoji: '🇬🇧' },
  { code: 'fr', name: 'French', emoji: '🇫🇷' },
  { code: 'fr-CA', name: 'French (Canada)', emoji: '🇨🇦' },
  { code: 'de', name: 'German', emoji: '🇩🇪' },
  { code: 'es', name: 'Spanish', emoji: '🇪🇸' },
  { code: 'es-MX', name: 'Spanish (Mexico)', emoji: '🇲🇽' },
  { code: 'it', name: 'Italian', emoji: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', emoji: '🇵🇹' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', emoji: '🇧🇷' },
  { code: 'nl', name: 'Dutch', emoji: '🇳🇱' },
  { code: 'ru', name: 'Russian', emoji: '🇷🇺' },
  { code: 'ar', name: 'Arabic', emoji: '🇸🇦' },
  { code: 'zh', name: 'Mandarin Chinese', emoji: '🇨🇳' },
  { code: 'ja', name: 'Japanese', emoji: '🇯🇵' },
  { code: 'ko', name: 'Korean', emoji: '🇰🇷' },
  { code: 'hi', name: 'Hindi', emoji: '🇮🇳' },
  { code: 'bn', name: 'Bengali', emoji: '🇧🇩' },
  { code: 'tr', name: 'Turkish', emoji: '🇹🇷' },
  { code: 'vi', name: 'Vietnamese', emoji: '🇻🇳' },
  { code: 'tl', name: 'Tagalog / Filipino', emoji: '🇵🇭' },
  { code: 'fa', name: 'Persian (Farsi)', emoji: '🇮🇷' },
  { code: 'pl', name: 'Polish', emoji: '🇵🇱' },
  { code: 'th', name: 'Thai', emoji: '🇹🇭' },
  { code: 'sv', name: 'Swedish', emoji: '🇸🇪' },
  { code: 'ro', name: 'Romanian', emoji: '🇷🇴' },
  { code: 'uk', name: 'Ukrainian', emoji: '🇺🇦' },
  { code: 'cs', name: 'Czech', emoji: '🇨🇿' },
  { code: 'lb', name: 'Luxembourgish', emoji: '🇱🇺' },
  { code: 'da', name: 'Danish', emoji: '🇩🇰' },
  { code: 'fi', name: 'Finnish', emoji: '🇫🇮' },
  { code: 'no', name: 'Norwegian', emoji: '🇳🇴' },
  // Add more languages as needed
];

// Helper function to get language details by code
const getLanguageByCode = (code: string) => {
  const language = languageOptions.find(lang => lang.code === code.toLowerCase());
  if (language) {
    return language;
  }
  return {
    code: code.toLowerCase(),
    name: code.toUpperCase(),
    emoji: '🌐'
  };
};

export const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  value,
  onChange,
  buttonLabel = 'Select Language',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Effect to close dropdown when clicking outside
  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = () => setIsOpen(false);
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen]);

  // Filter languages based on search term
  const filteredLanguages = languageOptions.filter(lang => 
    lang.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    lang.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLanguageSelect = (langCode: string) => {
    onChange(langCode);
    setIsOpen(false);
    setSearchTerm('');
  };

  const selectedLanguage = getLanguageByCode(value);

  return (
    <div className="relative">
      {/* Language Selection Dropdown Button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation(); // Prevent event from bubbling up
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left ${className}`}
      >
        {value ? (
          <span className="flex items-center gap-2">
            <span className="text-lg">{selectedLanguage.emoji}</span>
            <span className="text-gray-700">{selectedLanguage.name}</span>
          </span>
        ) : (
          <span className="text-gray-700">{buttonLabel}</span>
        )}
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div className="absolute left-0 right-0 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20 max-h-96 overflow-auto">
            {/* Search input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search languages..."
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            
            {/* Language options */}
            <div className="py-1">
              {filteredLanguages.map((lang) => (
                <button
                  type="button"
                  key={lang.code}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageSelect(lang.code);
                  }}
                  className={`w-full text-left px-4 py-2 ${
                    lang.code === value
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{lang.emoji}</span>
                    <div>
                      <div className="font-medium">{lang.name}</div>
                      <div className="text-xs text-gray-500">({lang.code})</div>
                    </div>
                  </div>
                </button>
              ))}
              {filteredLanguages.length === 0 && (
                <div className="px-4 py-2 text-sm text-gray-500">No languages found</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 